import React, { useState } from "react";
import { Text, Space, useMantineTheme, Box, Flex, Button } from "@mantine/core";
import styled from "styled-components";
import { shallow } from "zustand/shallow";
import { useTranslation } from "react-i18next";

import CheckInButton from "./CheckInButton";
import UserEffortView from "./UserEffortView";
import ViewTitle from "./ViewTitle";
import { ImageSlider } from "@components/shared";
import { Gauge, VuMeter, Speedometer } from "@components/DataViz";
import useAppStore from "./microsite-store";

export default function UserDashboard({ fetchUser }) {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  const { appConfig, coords, colors, entityInfo, user, setShowOtherEfforts } =
    useAppStore(
      (state) => ({
        appConfig: state.appConfig,
        coords: state.coords,
        colors: state.colors,
        entityInfo: state.entityInfo,
        user: state.user,
        setShowOtherEfforts: state.setShowOtherEfforts,
      }),
      shallow
    );

  const [contestSlideIndex, setContestSlideIndex] = useState(0);
  const [noLocation, setNoLocation] = useState(false);
  const efforts = user.campaign_efforts.filter((f) => f.entered);

  const selectedEffort = efforts.find(
    (f) => f.id === entityInfo.campaign_effort_id
  );

  const assets = entityInfo.configuration.assets
    ? entityInfo.configuration.assets
        .map((m) => ({
          ...m,
          asset: entityInfo.assets.find((f) => f.id === m.id),
        }))
        .filter((f) => f.asset)
    : [].filter((f) => f.asset);
  const sliderImages = assets.filter((f) => f.placement === 2);
  const contests = [...selectedEffort.contests].sort(
    (a, b) => b.retail_value_in_pennies - a.retail_value_in_pennies
  );

  // contest being displayed in slider
  const activeContest = contests[contestSlideIndex];

  return (
    <div>
      {sliderImages.length > 0 && (
        <ImageSlider
          items={[
            ...sliderImages.map((m) => ({
              src: m.asset.filename_url,
              key: m.id,
              external_link_url: m.asset.external_link_url,
            })),
          ]}
        />
      )}
      <Space h="sm" />
      <ViewTitle
        title={selectedEffort.title}
        subtitle={
          entityInfo.use_keywords
            ? t("microsite:user_effort_view_subtitle")
            : ""
        }
        marginBottom={"1em"}
        order={2}
      />
      <Flex justify="center" gap="xs">
        {coords && entityInfo.use_checkins && (
          <CheckInButton
            onSuccess={fetchUser}
            onNoLocation={() => setNoLocation(true)}
          />
        )}
        <Button
          onClick={() => {
            setShowOtherEfforts(true);
          }}
          radius="xl"
          variant="light"
        >
          {t("microsite:user_select_different_campaign_button")}
        </Button>
      </Flex>
      {/* <CheckinGauge>
        {coords && entityInfo.use_checkins && (
          <BoxWrapper hide={noLocation}>
            <CheckInButton
              onSuccess={fetchUser}
              onNoLocation={() => setNoLocation(true)}
            />
          </BoxWrapper>
        )}
      </CheckinGauge> */}
      {appConfig.components && activeContest && (
        <div>
          <div>
            <GaugeWrapper
              variant={
                appConfig.components ? appConfig.components.gauge_variant : null
              }
            >
              {appConfig.components?.gauge_variant === 1 && (
                <Speedometer
                  value={activeContest.count}
                  maxValue={activeContest.total_entries_allowed}
                />
              )}
              {appConfig.components?.gauge_variant === 3 && (
                <VuMeter
                  leftValue={activeContest.count}
                  micrositeMeterConfig={{
                    plotBands: [
                      {
                        from:
                          parseInt(activeContest.total_entries_allowed) * 0.65,
                        to: parseInt(activeContest.total_entries_allowed),
                        color: "var(--mantine-color-red-5)",
                        innerRadius: "100%",
                        outerRadius: "105%",
                      },
                    ],
                    // color: "var(--mantine-color-red-5)",
                    min: 0,
                    max: parseInt(activeContest.total_entries_allowed),
                  }}
                  // domain={[0, activeContest.total_entries_allowed]}
                  // variant={
                  //   appConfig.components
                  //     ? appConfig.components.gauge_variant
                  //     : null
                  // }
                  // fillColor={
                  //   theme.colors[colors.base]
                  //     ? theme.colors[colors.base][6]
                  //     : null
                  // }
                />
              )}
              {appConfig?.components?.gauge_variant === 2 && (
                <Gauge
                  value={activeContest.count}
                  domain={[0, activeContest.total_entries_allowed]}
                  maxValue={activeContest.total_entries_allowed}
                  variant={
                    appConfig.components
                      ? appConfig.components.gauge_variant
                      : null
                  }
                  fillColor={
                    theme.colors[colors.base]
                      ? theme.colors[colors.base][6]
                      : null
                  }
                />
              )}
            </GaugeWrapper>
            {activeContest && (
              <Box>
                <Text
                  c="white"
                  align="center"
                  style={{
                    wordWrap: "break-word",
                  }}
                  size="lg"
                  mw="63%"
                >
                  <b>{activeContest.name}</b>
                </Text>
                <Text size="sm" c="dimmed" align="center">
                  {activeContest.count} out of{" "}
                  {activeContest.total_entries_allowed}
                </Text>
              </Box>
            )}
          </div>
        </div>
      )}
      <Space h="xl" />
      <UserEffortView
        effort={selectedEffort}
        fetchData={fetchUser}
        contests={contests}
        onSlideChange={(e) => setContestSlideIndex(e)}
      />
    </div>
  );
}

const GaugeWrapper = styled.div`
  /* display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%; */

  svg {
    margin-bottom: ${(props) => (props.variant === 2 ? "25px" : "10px")};
  }
`;

const BoxWrapper = styled.div`
  display: ${(props) => (props.hide ? "none" : "flex")};
  justify-content: center;
  width: ${(props) => (props.hide ? "0%" : "100%")};
`;

const CheckinGauge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;
